import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, Icon, I, Pill, TEXT_WRAP_STYLE, Thumb, gridColumns, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import {
  downloadStockAllocationReport,
  getStockAllocationRun,
  listStockAllocationRunItems,
  listStockAllocationRuns,
  retryFailedStockAllocationItems,
} from '../stockAllocation/stockAllocationApi'
import type { StockAllocationRunDetail, StockAllocationRunItem, StockAllocationRunSummary } from '../stockAllocation/types'

interface StockAllocationActivityProps {
  accessToken: string
  onOpenProduct?: (productId: number) => void
}

type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral'

function statusTone(status: StockAllocationRunSummary['status']): PillTone {
  if (status === 'SUCCEEDED') return 'success'
  if (status === 'RUNNING') return 'info'
  if (status === 'PARTIAL_FAILURE') return 'warning'
  return 'danger'
}

export function StockAllocationActivity({ accessToken, onOpenProduct }: StockAllocationActivityProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [runs, setRuns] = useState<StockAllocationRunSummary[]>([])
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)
  const [detail, setDetail] = useState<StockAllocationRunDetail | null>(null)
  const [items, setItems] = useState<StockAllocationRunItem[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [detailError, setDetailError] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [retryMessage, setRetryMessage] = useState<string | null>(null)
  const detailRequestId = useRef(0)

  const selectRun = useCallback((runId: number) => {
    detailRequestId.current += 1
    setSelectedRunId(runId)
    setQuery('')
    setDebouncedQuery('')
    setCode('')
    setPage(0)
  }, [])

  const loadRuns = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const nextRuns = await listStockAllocationRuns(accessToken)
      setRuns(nextRuns)
      setSelectedRunId(current => current != null && nextRuns.some(run => run.id === current) ? current : nextRuns[0]?.id ?? null)
      if (nextRuns.length === 0) {
        setDetail(null)
        setItems([])
      }
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [accessToken])

  useEffect(() => {
    void Promise.resolve().then(loadRuns)
  }, [loadRuns])

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query), 250)
    return () => window.clearTimeout(timeout)
  }, [query])

  const loadDetail = useCallback(async () => {
    if (selectedRunId == null) return
    const requestId = ++detailRequestId.current
    setLoading(true)
    setDetailError(false)
    try {
      const [nextDetail, itemPage] = await Promise.all([
        getStockAllocationRun(accessToken, selectedRunId),
        listStockAllocationRunItems(accessToken, selectedRunId, { status: 'FAILED', code: code || undefined, query: debouncedQuery || undefined, page, size: 50 }),
      ])
      if (requestId !== detailRequestId.current) return
      setDetail(nextDetail)
      setItems(itemPage.content)
      setTotalItems(itemPage.totalElements)
      setTotalPages(itemPage.totalPages)
    } catch {
      if (requestId !== detailRequestId.current) return
      setDetail(null)
      setItems([])
      setDetailError(true)
    } finally {
      if (requestId === detailRequestId.current) setLoading(false)
    }
  }, [accessToken, selectedRunId, code, debouncedQuery, page])

  useEffect(() => {
    void Promise.resolve().then(loadDetail)
    return () => { detailRequestId.current += 1 }
  }, [loadDetail])

  async function retryFailed() {
    if (!detail || detail.run.failed === 0) return
    setRetrying(true)
    setRetryMessage(null)
    try {
      const retried = await retryFailedStockAllocationItems(accessToken, detail.run.id)
      setRetryMessage(String(t('stockAllocation.activity.retryAccepted', { id: retried.run.id })))
      await loadRuns()
      selectRun(retried.run.id)
    } catch {
      setRetryMessage(String(t('stockAllocation.activity.retryFailed')))
    } finally {
      setRetrying(false)
    }
  }

  async function downloadReport() {
    if (!detail) return
    setRetryMessage(null)
    try {
      const { blob, filename } = await downloadStockAllocationReport(accessToken, detail.run.id)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename ?? `stock-allocation-run-${detail.run.id}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      setRetryMessage(String(t('stockAllocation.activity.downloadFailed')))
    }
  }

  const formatDate = (value: string) => new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium', timeStyle: 'short',
  }).format(new Date(value))

  if (loading && runs.length === 0) {
    return <Card><div style={{ color: 'var(--fg-muted)' }}>{t('stockAllocation.activity.loading')}</div></Card>
  }
  if (error && runs.length === 0) {
    return <Card><div style={{ color: 'var(--danger)', fontWeight: 800 }}>{t('stockAllocation.activity.error')}</div><Button kind="secondary" size="sm" onClick={() => void loadRuns()} style={{ marginTop: 12 }}>{t('stockAllocation.activity.tryAgain')}</Button></Card>
  }
  if (runs.length === 0) {
    return <Card><div style={{ fontWeight: 800 }}>{t('stockAllocation.activity.empty.title')}</div><div style={{ color: 'var(--fg-muted)', marginTop: 4 }}>{t('stockAllocation.activity.empty.body')}</div></Card>
  }
  if (detailError) {
    return <Card><div role="alert" style={{ color: 'var(--danger)', fontWeight: 800 }}>{t('stockAllocation.activity.error')}</div><Button kind="secondary" size="sm" onClick={() => void loadDetail()} style={{ marginTop: 12 }}>{t('stockAllocation.activity.tryAgain')}</Button></Card>
  }

  const run = detail?.run
  const completion = run && run.attempted > 0 ? Math.round((run.pushed / run.attempted) * 100) : 0
  const actionStyle = mobileButtonStyle(isMobile)

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <label style={{ display: 'grid', gap: 6, maxWidth: 460 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-muted)' }}>{t('stockAllocation.activity.runLabel')}</span>
        <select value={selectedRunId ?? ''} onChange={event => selectRun(Number(event.target.value))} style={selectStyle}>
          {runs.map(candidate => <option key={candidate.id} value={candidate.id}>{candidate.connectionName ?? candidate.provider} · {formatDate(candidate.createdAt)} · {t(`stockAllocation.activity.status.${candidate.status}`)}</option>)}
        </select>
      </label>

      {run && (
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) auto'), gap: 16, alignItems: 'start' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--r-md)', display: 'grid', placeItems: 'center', background: run.failed > 0 ? 'var(--warning-100)' : 'var(--success-100)', color: run.failed > 0 ? 'var(--warning)' : 'var(--success)', fontWeight: 900 }}>
                  <Icon size={20}>{run.failed > 0 ? I.alert : I.check}</Icon>
                </div>
                <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 22, ...TEXT_WRAP_STYLE }}>
                  {t(run.failed > 0 && run.pushed > 0 ? 'stockAllocation.activity.partialTitle' : run.failed > 0 ? 'stockAllocation.activity.failedTitle' : 'stockAllocation.activity.successTitle', { provider: run.provider })}
                </h2>
                <Pill tone={statusTone(run.status)}>{t(`stockAllocation.activity.status.${run.status}`)}</Pill>
              </div>
              <div style={{ color: 'var(--fg-muted)', marginTop: 8 }}>{t(`stockAllocation.activity.trigger.${run.triggerType}`, { defaultValue: run.triggerType })} · {formatDate(run.createdAt)}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'repeat(4, minmax(0, 1fr))'), gap: 10, marginTop: 20 }}>
            <Metric value={run.attempted} label={t('stockAllocation.activity.metrics.attempted')} />
            <Metric value={run.pushed} label={t('stockAllocation.activity.metrics.pushed')} tone="success" />
            <Metric value={run.failed} label={t('stockAllocation.activity.metrics.failed')} tone="danger" />
            <Metric value={run.skipped} label={t('stockAllocation.activity.metrics.skipped')} />
          </div>

          <div style={{ marginTop: 18 }}>
            <div style={{ height: 8, background: 'var(--ink-100)', borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${completion}%`, height: '100%', background: 'var(--success)' }} /></div>
            <div style={{ color: 'var(--fg-muted)', marginTop: 8, fontSize: 13 }}>{t('stockAllocation.activity.progress', { percent: completion })}</div>
          </div>

          <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
            <Button kind="secondary" icon={I.download} onClick={() => void downloadReport()} style={actionStyle}>{t('stockAllocation.activity.download')}</Button>
            {run.provider === 'NOON' && run.failed > 0 && <Button kind="primary" icon={I.refresh} onClick={() => void retryFailed()} disabled={retrying} style={actionStyle}>{retrying ? t('stockAllocation.activity.retrying') : t('stockAllocation.activity.retry')}</Button>}
          </div>
          {retryMessage && <div role="status" style={{ color: 'var(--brand-700)', fontWeight: 800, marginTop: 10 }}>{retryMessage}</div>}
        </Card>
      )}

      {run && run.failed > 0 && (
        <section>
          <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(0, 1fr) auto auto'), gap: 10, alignItems: 'end', marginBottom: 12 }}>
            <div><h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{t('stockAllocation.activity.items.title')}</h2><div style={{ color: 'var(--fg-muted)', marginTop: 3, fontSize: 13 }}>{t('stockAllocation.activity.items.count', { count: totalItems })}</div></div>
            <input aria-label={String(t('stockAllocation.activity.items.search'))} placeholder={String(t('stockAllocation.activity.items.search'))} value={query} onChange={event => { setQuery(event.target.value); setPage(0) }} style={inputStyle} />
            <select aria-label={String(t('stockAllocation.activity.items.reason'))} value={code} onChange={event => { setCode(event.target.value); setPage(0) }} style={selectStyle}>
              <option value="">{t('stockAllocation.activity.items.allReasons')}</option>
              {detail?.failedCodes.map(group => <option key={group.code} value={group.code}>{reasonLabel(t, group.code)} ({group.count})</option>)}
            </select>
          </div>

          {isMobile ? (
            <div style={{ display: 'grid', gap: 10 }}>{items.map(item => <ItemCard key={item.id} item={item} onOpenProduct={onOpenProduct} />)}</div>
          ) : (
            <div style={{ overflow: 'hidden', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-lg)', background: 'white' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}><thead><tr><th style={thStyle}>{t('stockAllocation.activity.items.product')}</th><th style={thStyle}>{t('stockAllocation.activity.items.sku')}</th><th style={thStyle}>{t('stockAllocation.activity.items.reason')}</th><th style={thStyle}>{t('stockAllocation.activity.items.quantity')}</th><th style={thStyle}>{t('stockAllocation.activity.items.action')}</th></tr></thead><tbody>{items.map(item => <tr key={item.id}><td style={tdStyle}><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Thumb name={item.productName} size={36} /><strong>{item.productName}</strong></div></td><td style={{ ...tdStyle, fontFamily: 'var(--font-mono)' }}>{item.productSku}</td><td style={tdStyle}><div style={{ color: 'var(--danger)', fontWeight: 800 }}>{reasonLabel(t, item.providerCode ?? item.reason)}</div><div style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-subtle)', fontSize: 11 }}>{item.providerCode ?? item.reason}</div></td><td style={tdStyle}>{item.availableQuantity ?? '—'}</td><td style={tdStyle}>{onOpenProduct && <Button kind="ghost" size="sm" onClick={() => onOpenProduct(item.productId)}>{t('stockAllocation.activity.items.viewProduct')}</Button>}</td></tr>)}</tbody></table>
            </div>
          )}
          {items.length === 0 && <Card><div style={{ color: 'var(--fg-muted)' }}>{t('stockAllocation.activity.items.noMatches')}</div></Card>}
          {totalPages > 1 && <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 12 }}><Button kind="secondary" size="sm" disabled={page === 0} onClick={() => setPage(value => value - 1)}>{t('stockAllocation.activity.items.previous')}</Button><span style={{ color: 'var(--fg-muted)', fontSize: 13 }}>{t('stockAllocation.activity.items.page', { page: page + 1, pages: totalPages })}</span><Button kind="secondary" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(value => value + 1)}>{t('stockAllocation.activity.items.next')}</Button></div>}
        </section>
      )}
    </div>
  )
}

function Metric({ value, label, tone }: { value: number; label: string; tone?: 'success' | 'danger' }) {
  return <div style={{ padding: 14, borderRadius: 'var(--r-md)', background: tone === 'success' ? 'var(--success-100)' : tone === 'danger' ? 'var(--danger-100)' : 'var(--ink-50)' }}><div style={{ fontSize: 25, fontWeight: 900, color: tone === 'success' ? 'var(--success)' : tone === 'danger' ? 'var(--danger)' : 'var(--fg)' }}>{value.toLocaleString()}</div><div style={{ color: 'var(--fg-muted)', fontSize: 12 }}>{label}</div></div>
}

function ItemCard({ item, onOpenProduct }: { item: StockAllocationRunItem; onOpenProduct?: (productId: number) => void }) {
  const { t } = useTranslation()
  return <Card padding={16}><div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><Thumb name={item.productName} size={38} /><div style={{ minWidth: 0 }}><strong style={TEXT_WRAP_STYLE}>{item.productName}</strong><div style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-muted)', fontSize: 12 }}>{item.productSku}</div></div></div><div style={{ marginTop: 12, color: 'var(--danger)', fontWeight: 800 }}>{reasonLabel(t, item.providerCode ?? item.reason)}</div><div style={{ color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{item.providerCode ?? item.reason}</div>{onOpenProduct && <Button kind="ghost" size="sm" onClick={() => onOpenProduct(item.productId)} style={{ marginTop: 12 }}>{t('stockAllocation.activity.items.viewProduct')}</Button>}</Card>
}

function reasonLabel(t: ReturnType<typeof useTranslation>['t'], code: string | null): string {
  if (!code) return String(t('stockAllocation.activity.reasons.UNKNOWN'))
  return String(t(`stockAllocation.activity.reasons.${code}`, { defaultValue: code.replaceAll('_', ' ') }))
}

const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid var(--border-default)', borderRadius: 10, background: 'white', color: 'var(--fg)', fontFamily: 'inherit', fontSize: 13 } as const
const selectStyle = { ...inputStyle }
const thStyle = { padding: '11px 14px', textAlign: 'start', color: 'var(--fg-subtle)', background: 'var(--ink-50)', borderBottom: '1px solid var(--border-subtle)', fontSize: 11, letterSpacing: '0.08em' } as const
const tdStyle = { padding: '13px 14px', borderBottom: '1px solid var(--border-subtle)', color: 'var(--fg-muted)', fontSize: 13, textAlign: 'start' } as const
